import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useColor } from '@nuxt-common/media-card-large/app/modules/color';
import { refDebounced } from '@vueuse/core';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useProductType } from '~/composables/useProductType';
import { useBrand } from '~/composables/useBrand';
import { getUserIds } from '~/utils/cookiesUtils';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import useExperiments from '~/stores/useExperimentsStore';
import { useOutfittingStore } from '@nuxt-common/outfitting/app/stores/useOutfittingStore';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
// import useProductQVStore from '@nuxt-common/product-quickview/app/stores/useProductQVStore'

/**
 * Collects key props from dynamic sources and return them in one single data object.
 */
export default function useOutfittingData() {
  const store = useOutfittingStore();
  const { userId } = getUserIds();
  const { isMcom } = useBrand();
  const { isMobile } = useDeviceType();
  const { productData, productId } = useProduct();
  const { isShopTheSetExpEnabled } = useFeatureEligibility();
  const { isMasterProductFlag, beautyFlag, homeFOBFlag, isConsolidatedThirdVariantFlag } = useProductFlags();
  const consolidatedMemberStore = useConsolidatedMemberStore();

  const { memberProduct } = storeToRefs(consolidatedMemberStore);

  const selectedProductId = useSelectedMemberId();
  const { selectedColor } = useColor(isShopTheSetExpEnabled ? selectedProductId : productId);

  const { isConsolidatedPage } = useProductType();

  const { killswitches } = useKillswitches();

  const debouncedSelectedColor = refDebounced(selectedColor, 500);
  const product = computed(() => (isShopTheSetExpEnabled ? memberProduct.value : productData));

  const {
    experimentFlags: { isShopSimilarNavigationEnabled, isImageIndicatorStylingEnabled },
  } = useExperiments();

  const outfittingProps = computed(() => ({
    userId,
    brand: isMcom ? 'mcom' : 'bcom',
    pageType: 'pdp',
    // TODO: Temporary fix for the outfitting widget; it is expected to be reactive.
    // Remove this once the widget is updated to accept a non-reactive prop.
    productData: product,
    // value is not nullish, although it could be falsy
    ...((!!isConsolidatedThirdVariantFlag || isShopTheSetExpEnabled) && { selectedProductId: selectedProductId.value }),
    selectedColorName: debouncedSelectedColor.value?.name || '',
    isMobile: isMobile.value,
    featureFlags: {
      isMasterProduct: !isShopTheSetExpEnabled && isMasterProductFlag,
      isBeautyProduct: beautyFlag,
      isHomeFOB: homeFOBFlag,
      isConsolidatedThirdVariant: isConsolidatedThirdVariantFlag,
      isConsolidatedPage,
      isShopSimilarNavigationEnabled,
      isPairItWithCopyEnabled: killswitches.isPairItWithCopyEnabled,
      isImageIndicatorStylingEnabled,
    },
  }));

  // Checking outfitsAvailability to prevent Stylitics widget from attempting to load
  // if we already know above the fold whether the CTA should show or not.
  const hasOutfitsAvailable = computed(() => store.outfitsAvailability);

  // validating all required data to display Outfitting widget
  const enableOutfittingWidget = computed(() => killswitches.enableOutfitting && hasOutfitsAvailable.value && product.value);

  return {
    outfittingProps,
    enableOutfittingWidget,
  };
}
